import { BaseMsgProps } from './types';
import { TextMsg } from './TextMsg';
import { useTranslate } from '@tolgee/react';
import { reverse, uniq } from 'lodash-es';

import { Button } from 'antd';
import styled from '@emotion/styled';
import { LineWrapper, MessageBody } from 'components/Dialogue';
import { memo, useMemo } from 'react';

const MESSAGE_TYPE_KEY_MAP: Record<string, string> = {
  IMAGE: 'domain.message.type.image',
  VIDEO: 'domain.message.type.video',
  STICKER: 'domain.message.type.sticker',
  ITEM: 'domain.message.type.product',
  FAQ_LIVEAGENT: 'domain.message.type.faqLiveAgent',
  VARIATION_CARD: 'domain.message.type.variationCard',
  SYS_ASSIGN: 'domain.message.systemType.assign',
  SYS_WA_REJECT: 'domain.message.systemType.whatsAppRejected',
  SYS_INIT_CONVERSATION: 'scene.chatWorkspace.messageThread.systemMessage.initiatedConversation',
  SYS_FAQ_QUESTION: 'scene.chatWorkspace.messageThread.systemMessage.faqQuestion',
  SHOPEE_RETURN_REFUND_CARD: 'scene.chatWorkspace.messageThread.systemMessage.shopeeReturnRefund',
  recall: 'scene.chatWorkspace.messageThread.systemMessage.recall',
};

const getMessageTypeKey = (type: string): string =>
  MESSAGE_TYPE_KEY_MAP[type] || 'scene.chatWorkspace.messageThread.feedback.unknownMessageType';

export function useNouseMessageTypeLabelMap() {
  const { t } = useTranslate();

  const typeLabelMap = useMemo(() => {
    return {
      SELLER_CENTER_AUTO_REPLY: t(
        'scene.chatWorkspace.messageThread.divider.sellerCenterAutoReply',
      ),
      WELCOME_REPLY: t('domain.aiReception.type.storeWelcomeMessage'),
      TIMEOUT_AUTO_REPLY: t('domain.aiReception.type.autoReply'),
      ORDER_REMINDER: t('domain.aiReception.type.orderReminder'),
      PAYMENT_REMINDER: t('scene.chatWorkspace.aiReception.action.paymentReminder'),
      LOGISTICS_CARE: t('domain.aiReception.type.logisticsCare'),
      POSITIVE_REVIEW_CARE: t('domain.aiReception.type.positiveReviewFollowUp'),
      WATCH_LIMIT: t('domain.aiReception.type.onDutyAutoReply'),
      PROACTIVELY_CONTACT_BUYER: t(
        'scene.chatWorkspace.aiReception.scenarioPreview.action.contactBuyer',
      ),
      PRAISE_REMINDER: t('scene.chatWorkspace.aiReception.action.reviewInvitation'),
    } as const;
  }, [t]);

  return {
    t,
    typeLabelMap,
  };
}

function FoldMsg({
  raw,
  msg,
  conversation,
  nouseMessageList,
  onExpand,
}: BaseMsgProps & {
  nouseMessageList: Conversation.NouseMessage[];
  onExpand: () => void;
}) {
  const { t, typeLabelMap } = useNouseMessageTypeLabelMap();

  const typeList = reverse(uniq(nouseMessageList.map((_) => _.type)));

  const tips =
    nouseMessageList.length === 1
      ? t('scene.chatWorkspace.messageThread.divider.foldedMessagesSingle', {
          x: typeLabelMap[nouseMessageList[0].type],
        })
      : t('scene.chatWorkspace.messageThread.divider.foldedMessagesCount', {
          x: typeList
            .slice(0, 3)
            .map((_) => `[${typeLabelMap[_]}]`)
            .join('、'),
          n: nouseMessageList.length,
        });

  const footer = (
    <Footer>
      {tips}
      <Button type="link" onClick={onExpand}>
        {t('common.action.clickToView')}
      </Button>
    </Footer>
  );

  if (msg.type === 'text') {
    return (
      <LineWrapper layout="right">
        <MessageBody layout="right">
          <Wrapper>
            <TextMsg isFold raw={raw} msg={msg} conversation={conversation} />
          </Wrapper>
          {footer}
        </MessageBody>
      </LineWrapper>
    );
  }

  return (
    <LineWrapper layout="right">
      <MessageBody layout="right">
        <MessageType>
          [
          {t({
            key: getMessageTypeKey(raw.messageType),
            defaultValue: t('scene.chatWorkspace.messageThread.feedback.unknownMessageType'),
          })}
          ]
        </MessageType>
        {footer}
      </MessageBody>
    </LineWrapper>
  );
}

const MessageType = styled.div`
  padding: 12px;
  background: #fff;
  border-radius: 4px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  color: #454d5a;
  > button {
    padding-left: 8px;
    padding-right: 0;
  }
`;

const Wrapper = styled.div`
  max-width: 400px;
  > div {
    max-width: 100%;
  }
`;

export default memo(FoldMsg);
